import type { ComponentProps } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useUIVersion } from '@/utils/UIVersionContext'
import { Menu as MenuPrimitive } from '@base-ui/react/menu'
import { Button } from 'components/ui/button'
import { Monitor, Moon, Sun } from 'lucide-react'

import { useTheme } from './theme-provider'

export function ThemeToggle({
  side = 'top',
  expanded,
  align = 'center',
  labelPlacement = 'inline',
  triggerClassName,
  triggerSize = 'default',
  contentClassName,
  iconClassName,
  labelClassName,
}: {
  side?: MenuPrimitive.Positioner.Props['side']
  align?: MenuPrimitive.Positioner.Props['align']
  expanded?: boolean
  labelPlacement?: 'inline' | 'below'
  triggerClassName?: string
  triggerSize?: ComponentProps<typeof Button>['size']
  contentClassName?: string
  iconClassName?: string
  labelClassName?: string
} = {}) {
  const { isNewUI } = useUIVersion()
  const { theme, resolvedTheme, setTheme } = useTheme()

  const triggerLabel = theme === 'system' ? `System (${resolvedTheme})` : theme
  const TriggerIcon = theme === 'system' ? Monitor : resolvedTheme === 'dark' ? Moon : Sun
  const showLabelBelow = labelPlacement === 'below'
  const resolvedTriggerClassName =
    triggerClassName ??
    cn(
      'w-full',
      showLabelBelow ? 'h-full flex-col justify-center gap-0.5 px-1 text-xs' : 'justify-start'
    )
  const resolvedContentClassName =
    contentClassName ??
    cn('flex items-center', showLabelBelow ? 'flex-col justify-center gap-0.5' : 'gap-1.5')
  const resolvedIconClassName = iconClassName ?? 'size-5'
  const resolvedLabelClassName =
    labelClassName ?? cn('text-center leading-tight', showLabelBelow && 'text-xs')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        delay={25}
        openOnHover
        render={
          <Button
            variant='ghost'
            size={triggerSize}
            type='button'
            className={resolvedTriggerClassName}
            aria-label={`Theme: ${triggerLabel}`}
            title={`Theme: ${triggerLabel}`}
          />
        }
      >
        <div className={resolvedContentClassName}>
          <TriggerIcon className={resolvedIconClassName} />
          {expanded && <span className={resolvedLabelClassName}>Theme</span>}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} side={side} className='w-44'>
        <DropdownMenuGroup>
          <DropdownMenuLabel>Theme</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={theme}
            onValueChange={(value) => {
              if (value === 'light' || value === 'dark' || value === 'system') {
                setTheme(value)
              }
            }}
          >
            <DropdownMenuRadioItem value='light'>
              <Sun className='h-4 w-4' />
              Light
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value='dark' disabled={!isNewUI}>
              <Moon className='h-4 w-4' />
              Dark
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value='system'>
              <Monitor className='h-4 w-4' />
              System
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
